import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PendingEdits,
  SectionPrompt,
} from 'components/common/configPanel/UnsavedSectionPrompt';

// Unsaved edits in the open rules editor. Anything that takes the user off
// this view asks first — the same Save-or-discard question the configuration
// screens ask. The editor reports its edits through pendingEditsRef; the page
// owns the guard because switching tabs is one of the ways out.
export const useRouteLeaveGuard = () => {
  const navigate = useNavigate();
  const pendingEditsRef = useRef<PendingEdits | null>(null);
  const [leavePrompt, setLeavePrompt] = useState<SectionPrompt | null>(null);
  const hasEdits = () => !!pendingEditsRef.current?.hasEdits();

  const guardedLeave = (go: () => void) => {
    const pending = pendingEditsRef.current;
    if (!pending?.hasEdits()) return go();
    setLeavePrompt({
      onSave: () => pending.save(),
      onDiscard: () => pending.discard(),
      proceed: go,
    });
  };

  // Links outside the page (the top nav, breadcrumbs) can't call
  // guardedLeave, so in-app link clicks are caught on the way down, before
  // react-router's own handler. Plain left clicks only: a new-tab click
  // leaves this page as it is.
  const guardedLeaveRef = useRef(guardedLeave);
  guardedLeaveRef.current = guardedLeave;
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (!hasEdits()) return;
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      )
        return;
      const anchor = (event.target as Element | null)?.closest?.('a[href]');
      if (!anchor || anchor.getAttribute('target') === '_blank') return;
      const url = new URL((anchor as HTMLAnchorElement).href);
      if (url.origin !== window.location.origin) return;
      const to = `${url.pathname}${url.search}${url.hash}`;
      if (to === `${window.location.pathname}${window.location.search}`) return;
      event.preventDefault();
      event.stopPropagation();
      guardedLeaveRef.current(() => navigate(to));
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reloading or closing the tab can only get the browser's own prompt.
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasEdits()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    pendingEditsRef,
    guardedLeave,
    leavePrompt,
    closeLeavePrompt: () => setLeavePrompt(null),
  };
};
